import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import './Legal.css';

const Terms: React.FC = () => {
  return (
    <div className="legal-page">
      <div className="legal-header">
        <div className="container">
          <Link to="/" className="back-link">
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </Link>
          <div className="legal-title-section">
            <FileText size={48} className="legal-icon" />
            <h1>Terms of Service</h1>
            <p className="legal-subtitle">Last updated: January 2024</p>
          </div>
        </div>
      </div>

      <div className="legal-content">
        <div className="container">
          <div className="legal-body">
            <section>
              <h2>1. Acceptance of Terms</h2>
              <p>
                By accessing or using Flazu API ("Service"), you agree to be bound by these Terms of Service ("Terms"). 
                If you do not agree to these Terms, do not use the Service.
              </p>
              <p>
                These Terms constitute a legally binding agreement between you and Flazu API ("we," "us," or "our").
              </p>
            </section>

            <section>
              <h2>2. Description of Service</h2>
              <p>Flazu API provides:</p>
              <ul>
                <li>Unified API gateway to multiple AI models (GPT-4, Claude, Gemini, etc.)</li>
                <li>OpenAI-compatible API endpoints</li>
                <li>Usage tracking and analytics</li>
                <li>Security features (IP locking, rate limiting)</li>
                <li>API key management dashboard</li>
              </ul>
            </section>

            <section>
              <h2>3. Account Registration</h2>
              <h3>3.1 Account Creation</h3>
              <p>To use the Service, you must:</p>
              <ul>
                <li>Be at least 18 years old</li>
                <li>Provide accurate and complete information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Use a valid redeem code to activate your account</li>
              </ul>

              <h3>3.2 Account Security</h3>
              <p>You are responsible for:</p>
              <ul>
                <li>Maintaining the confidentiality of your API keys</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized use</li>
              </ul>
            </section>

            <section>
              <h2>4. Acceptable Use Policy</h2>
              <h3>4.1 Permitted Use</h3>
              <p>You may use the Service for:</p>
              <ul>
                <li>Legitimate business or personal projects</li>
                <li>Integration with your applications</li>
                <li>Testing and development</li>
              </ul>

              <h3>4.2 Prohibited Activities</h3>
              <p>You may NOT:</p>
              <ul>
                <li>Reverse engineer, decompile, or disassemble the Service</li>
                <li>Bypass rate limits or security measures</li>
                <li>Share your API keys with unauthorized parties</li>
                <li>Use the Service for illegal activities</li>
                <li>Generate spam, malware, or harmful content</li>
                <li>Scrape or harvest data from the Service</li>
                <li>Resell or redistribute the Service without permission</li>
                <li>Use the Service to compete with us</li>
                <li>Violate intellectual property rights</li>
                <li>Create fake or fraudulent accounts</li>
              </ul>
            </section>

            <section>
              <h2>5. Pricing and Payment</h2>
              <h3>5.1 Plans and Fees</h3>
              <ul>
                <li><strong>FREE Plan:</strong> $0/month with limited features</li>
                <li><strong>PRO Plan:</strong> $30/month</li>
                <li><strong>ENTERPRISE Plan:</strong> $200/month</li>
              </ul>

              <h3>5.2 Billing</h3>
              <ul>
                <li>Plans are billed monthly in advance</li>
                <li>Redeem codes are required for activation</li>
                <li>Automatic renewal unless cancelled</li>
                <li>All prices are in USD</li>
              </ul>

              <h3>5.3 Refunds</h3>
              <p>
                <strong>All sales are final. We do not offer refunds.</strong> 
                Please test with the FREE plan before purchasing a paid plan.
              </p>

              <h3>5.4 Price Changes</h3>
              <p>
                We reserve the right to change pricing with 30 days notice. 
                Existing subscribers will be notified via email.
              </p>
            </section>

            <section>
              <h2>6. Rate Limits and Usage</h2>
              <p>Each plan has specific limits:</p>
              <ul>
                <li><strong>FREE:</strong> 10 requests/min, 16,384 tokens/request</li>
                <li><strong>PRO:</strong> 100 requests/min, 32,768 tokens/request</li>
                <li><strong>ENTERPRISE:</strong> 6,000 requests/min, 65,536 tokens/request</li>
              </ul>
              <p>
                Exceeding rate limits will result in 429 errors. 
                Repeated violations may result in account suspension.
              </p>
            </section>

            <section>
              <h2>7. Intellectual Property</h2>
              <h3>7.1 Our Rights</h3>
              <p>All source code, design, API endpoints, and documentation are proprietary and protected under:</p>
              <ul>
                <li>Copyright Act 1987 (Malaysia)</li>
                <li>Computer Crimes Act 1997 (Malaysia)</li>
                <li>International copyright treaties</li>
              </ul>

              <h3>7.2 License Grant</h3>
              <p>
                We grant you a limited, non-exclusive, non-transferable license to use the Service 
                subject to these Terms. This license does NOT include the right to:
              </p>
              <ul>
                <li>Copy or modify our code</li>
                <li>Create derivative works</li>
                <li>Distribute or sublicense the Service</li>
              </ul>

              <h3>7.3 DMCA Policy</h3>
              <p>
                Unauthorized copying or distribution is strictly prohibited. 
                Violations will result in:
              </p>
              <ul>
                <li>Immediate account termination</li>
                <li>Legal action (civil and criminal)</li>
                <li>Damages of up to RM100,000 per violation</li>
              </ul>
              <p>DMCA Agent: <a href="mailto:dmca@flazu.com">dmca@flazu.com</a></p>
            </section>

            <section>
              <h2>8. Service Availability</h2>
              <h3>8.1 Uptime</h3>
              <p>
                We strive for 99.9% uptime but do not guarantee uninterrupted service. 
                Scheduled maintenance will be announced in advance.
              </p>

              <h3>8.2 Service Modifications</h3>
              <p>
                We reserve the right to modify, suspend, or discontinue the Service at any time 
                with or without notice.
              </p>
            </section>

            <section>
              <h2>9. Termination</h2>
              <h3>9.1 By You</h3>
              <p>You may cancel your account at any time through the dashboard.</p>

              <h3>9.2 By Us</h3>
              <p>We may suspend or terminate your account if you:</p>
              <ul>
                <li>Violate these Terms</li>
                <li>Engage in fraudulent activity</li>
                <li>Fail to pay fees</li>
                <li>Pose a security risk</li>
              </ul>

              <h3>9.3 Effect of Termination</h3>
              <ul>
                <li>Your API keys will be immediately revoked</li>
                <li>Access to the dashboard will be disabled</li>
                <li>No refunds will be issued</li>
                <li>We may delete your data after 30 days</li>
              </ul>
            </section>

            <section>
              <h2>10. Disclaimer of Warranties</h2>
              <p>
                THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. 
                WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING:
              </p>
              <ul>
                <li>Merchantability</li>
                <li>Fitness for a particular purpose</li>
                <li>Non-infringement</li>
                <li>Accuracy or reliability of results</li>
              </ul>
            </section>

            <section>
              <h2>11. Limitation of Liability</h2>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR:
              </p>
              <ul>
                <li>Indirect, incidental, or consequential damages</li>
                <li>Loss of profits, data, or business opportunities</li>
                <li>Service interruptions or errors</li>
                <li>Third-party actions</li>
              </ul>
              <p>
                Our total liability shall not exceed the amount you paid us in the last 12 months.
              </p>
            </section>

            <section>
              <h2>12. Indemnification</h2>
              <p>
                You agree to indemnify and hold us harmless from any claims, damages, or expenses 
                arising from your:
              </p>
              <ul>
                <li>Use of the Service</li>
                <li>Violation of these Terms</li>
                <li>Violation of any third-party rights</li>
                <li>Unlawful activities</li>
              </ul>
            </section>

            <section>
              <h2>13. Governing Law</h2>
              <p>
                These Terms are governed by the laws of Malaysia. 
                Any disputes shall be resolved in the courts of Kuala Lumpur, Malaysia.
              </p>
            </section>

            <section>
              <h2>14. Changes to Terms</h2>
              <p>
                We may update these Terms at any time. Material changes will be notified via:
              </p>
              <ul>
                <li>Email to your registered address</li>
                <li>Notice on our website</li>
              </ul>
              <p>
                Continued use of the Service after changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2>15. Contact Information</h2>
              <p>For questions about these Terms:</p>
              <ul>
                <li>Email: <a href="mailto:legal@flazu.com">legal@flazu.com</a></li>
                <li>Support: <a href="mailto:support@flazu.com">support@flazu.com</a></li>
                <li>DMCA: <a href="mailto:dmca@flazu.com">dmca@flazu.com</a></li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;